import axios from 'axios';

import { API_BASE_URL } from '../api';
import { auth } from '../firebase/firebase';
import { NotificationChannel } from './notifications';

export interface NotificationChannelSettings {
  enabled: boolean;
  priority: number;
}

export interface NotificationReportTypes {
  standaloneDailyReport?: boolean;
}

export interface NotificationSettings {
  enabled: boolean;
  channels: Record<NotificationChannel, NotificationChannelSettings>;
  reportTypes?: NotificationReportTypes;
}

export const fetchNotificationSettings = async (): Promise<NotificationSettings> => {
  const token = await auth.currentUser?.getIdToken();
  const response = await axios.get(`${API_BASE_URL}/api/user/notification-settings`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

export const updateNotificationSettings = async (settings: NotificationSettings): Promise<NotificationSettings> => {
  const token = await auth.currentUser?.getIdToken();
  // The backend validates the full NotificationSettings object, so callers must
  // GET the current settings, mutate them, and PUT the complete object back.
  const response = await axios.put(`${API_BASE_URL}/api/user/notification-settings`, settings, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

// MF-60 — opt-in (default false) consent for the weekly ADR drift-check job, which clones and
// runs an LLM investigation over the user's repo in the background. Per-user, not per-project.
export const fetchAdrDriftCheckSetting = async (): Promise<{ enabled: boolean }> => {
  const token = await auth.currentUser?.getIdToken();
  const response = await axios.get(`${API_BASE_URL}/api/user/adr-drift-check-setting`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

export const updateAdrDriftCheckSetting = async (enabled: boolean): Promise<{ enabled: boolean }> => {
  const token = await auth.currentUser?.getIdToken();
  const response = await axios.put(
    `${API_BASE_URL}/api/user/adr-drift-check-setting`,
    { enabled },
    { headers: { Authorization: `Bearer ${token}` } },
  );
  return response.data;
};

// MF-64 — global reviewer-agent setting. Watches all connected repos' PRs, leaves reviews,
// and runs on a 10-minute schedule. Default OFF.
export const fetchGitReviewerSetting = async (): Promise<{ enabled: boolean }> => {
  const token = await auth.currentUser?.getIdToken();
  const response = await axios.get(`${API_BASE_URL}/api/user/git-reviewer-setting`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

export const updateGitReviewerSetting = async (enabled: boolean): Promise<{ enabled: boolean }> => {
  const token = await auth.currentUser?.getIdToken();
  const response = await axios.put(
    `${API_BASE_URL}/api/user/git-reviewer-setting`,
    { enabled },
    { headers: { Authorization: `Bearer ${token}` } },
  );
  return response.data;
};
